import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  arrayOf,
  bool,
  customType,
  extend,
  params,
  string as stringType
} from '@thd-nucleus/data-sources';
import { getPageType } from '../../../utils/window-utils';
import { getRandomElements } from '../../../utils/common';
import { GENERAL_QUESTIONS, PIP_QUESTIONS } from './constants';
import { useAssistant } from '../AssistantProvider';
import { TileButton } from '../../common/TileButton';
import { AssistantLoader } from './drawer/AssistantLoader';
import { PAGE_TYPES } from '../constants';

const randomSuggestQuestions = getRandomElements(getPageType() === PAGE_TYPES.PIP ? PIP_QUESTIONS : GENERAL_QUESTIONS);
// Random suggested questions logic was moved outside the component to get always the same questions
// for each instance of the component
const SuggestedQuestions = forwardRef(({
  onClick,
  withAskPrompt,
  handleAskPromptClick,
  truncate,
}, ref) => {
  const { messages, isSuggestedQuestionsLoading, wereQuestionsGenerated } = useAssistant();

  // eslint-disable-next-line max-len
  const questionClasses = 'sui-flex sui-gap-4 sui-content-stretch sui-mb-4 [@media(min-height:101px)]:sui-mt-2 sui-items-start [@media(max-height:100px)]:sui-flex-row [@media(max-height:100px)]:sui-px-4 [@media(max-height:100px)]:sui-overflow-x-scroll [@media(min-height:101px)]:sui-flex-col';
  const lastMessage = messages[messages.length - 1];
  const suggestedQuestions = lastMessage?.content?.suggestedQuestions?.length > 0
    ? lastMessage.content.suggestedQuestions
    : randomSuggestQuestions;
  const askPrompt = 'Ask a question';

  return wereQuestionsGenerated && (
    isSuggestedQuestionsLoading
      ? (<AssistantLoader loading={isSuggestedQuestionsLoading} />)
      : (
        <div className={questionClasses} data-testid="suggested-questions" ref={ref}>
          {withAskPrompt && (
            <div className="sui-flex sui-shrink-0 sui-grow" key="ask-prompt">
              <TileButton
                onClick={handleAskPromptClick}
                value={askPrompt}
                data-testid="suggested-question"
                truncate={truncate}
              />
            </div>
          )}
          {suggestedQuestions?.map((question, i) => ([
            <div className="sui-flex sui-shrink-0 sui-grow" key={i}>
              <TileButton
                onClick={() => onClick(question, i, lastMessage?.isInitialMessage)}
                value={question}
                data-testid="suggested-question"
                truncate={truncate}
                withIcon
              />
            </div>
          ]))}
        </div>
      )
  );
});

SuggestedQuestions.propTypes = {
  withAskPrompt: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  handleAskPromptClick: PropTypes.func,
  truncate: PropTypes.bool,
};

SuggestedQuestions.defaultProps = {
  withAskPrompt: false,
  handleAskPromptClick: () => {},
  truncate: false,
};

SuggestedQuestions.dataModel = extend(
  {
    generateQuestions: params({
      userContext: customType('UserContext')
        .shape({
          userIdentities: customType('UserIdentities')
            .shape({
              mcvisId: stringType(),
              svocId: stringType()
            }),
          sessionId: stringType(),
          storeId: stringType(),
          zipCode: stringType(),
          deviceType: stringType(),
          language: stringType()
        }),
      pageContext: customType('PageContext')
        .shape({
          pageType: stringType(),
          referrerPage: stringType(),
          timestamp: stringType(),
          url: stringType(),
        }),
      questionContext: customType('QuestionContext')
        .shape({
          userQuestion: stringType(),
          user_typed: stringType(),
          itemId: stringType(),
          recentSearchTerms: arrayOf(stringType()),
        }),
      suggestedQuestions: bool(),
      followUpQuestions: bool(),
      model_name: stringType(),
    })
      .mutation()
      .shape({
        suggestedQuestions: arrayOf(stringType()),
        additionalDetails: customType('MagicApronAdditionalDetails')
          .shape({
            inquiryDetails: customType('MagicApronInquiryDetails')
              .shape({
                telephoneNumber: stringType(),
                description: stringType(),
                referenceUrl: stringType()
              })
          }),
      })
  });

export { SuggestedQuestions };
